<template>
  <footer class="bg-bhutan-green text-white footer">
    <div class="container mx-auto px-4 py-12">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        <!-- Logo and contact info -->
        <div class="space-y-4">
          <img src="@/assets/img/rgob.png" alt="Invest Bhutan Logo" class="h-28 w-auto mb-4" />
          <h3 class="text-2xl font-bold">Invest Bhutan</h3>
          <div class="space-y-2">
            <p class="flex items-center"><Mail class="mr-2 h-5 w-5" /> {{ contactInfo.email }}</p>
            <p class="flex items-center"><Phone class="mr-2 h-5 w-5" /> {{ contactInfo.phone }}</p>
            <p class="flex items-center">
              <MapPin class="mr-2 h-5 w-5" /> {{ contactInfo.address }}
            </p>
            <p class="mb-4">Follow us to stay up-to-date with our latest news and events.</p>
            <div class="flex space-x-4">
              <a
                href="https://facebook.com/investbhutan"
                class="text-white hover:text-green-200 transition-colors duration-300"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook class="h-6 w-6" />
              </a>
              <a
                href="https://www.linkedin.com/company/invest-bhutan-department-of-industry/"
                class="text-white hover:text-green-200 transition-colors duration-300"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Linkedin class="h-6 w-6" />
              </a>
              <a
                href="https://youtube.com/@InvestBhutan"
                class="text-white hover:text-green-200 transition-colors duration-300"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <Youtube class="h-6 w-6" />
              </a>
            </div>
          </div>
        </div>

        <!-- Quick Links -->
        <div>
          <h4 class="text-xl font-semibold mb-4">Quick Links</h4>
          <ul class="space-y-2">
            <li v-for="link in quickLinks" :key="link.name">
              <a
                :href="link.url"
                class="hover:text-green-200 transition-colors duration-300 flex items-center"
              >
                <ChevronsRight class="mr-2 h-4 w-4" /> {{ link.name }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Popular Links -->
        <div>
          <h4 class="text-xl font-semibold mb-4">Popular Links</h4>
          <ul class="space-y-2">
            <li v-for="link in popularLinks" :key="link.name">
              <a
                :href="link.url"
                target="_blank"
                rel="noopener noreferrer"
                class="hover:text-green-200 transition-colors duration-300 flex items-center"
              >
                <ExternalLink class="mr-2 h-4 w-4" /> {{ link.name }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Newsletter and Social Links -->
        <div>
          <h4 class="text-xl font-semibold mb-4">Our Location</h4>
          <div class="aspect-square relative overflow-hidden rounded-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d7079.807223838137!2d89.63391!3d27.47226!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39e1941a1b18fb85%3A0x502230415ce0e727!2sDepartment%20of%20Industry!5e0!3m2!1sen!2sbt!4v1721563921653!5m2!1sen!2sbt"
              width="100%"
              height="100%"
              style="border: 0; position: absolute; top: 0; left: 0"
              allowfullscreen="true"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </div>

    <!-- Copyright area and Visitor Counter -->
    <div class="bg-bhutan-green-light py-6">
      <div class="container mx-auto px-4 flex flex-col md:flex-row justify-between items-center">
        <div>
          <p>© {{ currentYear }} Invest Bhutan. All rights reserved.</p>
          <p class="text-[8px] text-transparent non-selectable">
            Designed by Ujwal Hollica Pradhan. Call +975-17795088
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref, onMounted } from 'vue'
// @ts-ignore
import api from '@/services/api.js'
import {
  Mail,
  Phone,
  MapPin,
  ChevronsRight,
  ExternalLink,
  Facebook,
  Linkedin,
  Youtube
} from 'lucide-vue-next'

const contactInfo = ref({
  email: 'investbhutan@moice.gov.bt',
  phone: '+975-2-330435 / 337623 / 337624',
  address: 'Department of Industry, MOICE'
})

const quickLinks = ref([
  { name: 'About Us', url: '/about' },
  { name: 'Service Provider', url: '/service-provider' },
  { name: 'FAQs', url: '/faqs' },
  { name: 'Login', url: '/login' },
  { name: 'Register', url: '/register' }
])

const popularLinks = ref([
  { name: 'G2C Service', url: 'https://www.citizenservices.gov.bt/' },
  { name: 'Integrated Business Licensing System', url: 'https://ibls.systems.gov.bt/' },
  { name: 'Ministry of Industry, Commerce and Employment', url: 'https://www.moice.gov.bt/' },
  { name: 'Industry Information System', url: 'https://iis.industry.gov.bt/' },
  { name: 'Department of Industry', url: 'https://industry.gov.bt/' }
])

const currentYear = ref(new Date().getFullYear()) // Make currentYear reactive

onMounted(async () => {
  await trackVisitor() // Track visitor and increment counts
})

async function trackVisitor() {
  try {
    const payload = {
      ip_address: await getIpAddress(),
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer
    }

    await api.post('webcontents/api/visitor-statistics-summary/', payload)
  } catch (error) {
    console.error('Error tracking visitor:', error)
  }
}

async function getIpAddress() {
  try {
    const response = await fetch('https://api.ipify.org?format=json')
    const data = await response.json()
    return data.ip
  } catch (error) {
    console.error('Error fetching IP:', error)
    return '0.0.0.0' // Default value if IP fetch fails
  }
}

function getSessionId() {
  let sessionId = localStorage.getItem('session_id')
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2)
    localStorage.setItem('session_id', sessionId)
  }
  return sessionId
}
</script>

<style scoped>
.footer {
  background-image: url('@/assets/img/footer.png');
  background-size: cover;
  background-position: center;
  background-blend-mode: overlay;
}

.non-selectable {
  user-select: none;
  pointer-events: none;
}
</style>